import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import BankDetailsForm from '../components/BankDetailsForm';
import AgreementModal from '../components/AgreementModal';
import { Shield, FileText, Download, CheckCircle2, ExternalLink } from 'lucide-react';
import { API_URL as API } from '../config/api';

export default function Profile() {
  const { user, token, login, logout } = useAuth();
  const navigate = useNavigate();

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [agreement, setAgreement] = useState(null);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [agreementLoading, setAgreementLoading] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    setName(user.name || '');

    // Fetch user's signed agreement
    if (token) {
      setAgreementLoading(true);
      axios.get(`${API}/agreements/my`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => {
        if (res.data.agreements && res.data.agreements.length > 0) {
          setAgreement(res.data.agreements[0]);
        }
      })
      .catch(err => console.error("Error fetching agreements:", err))
      .finally(() => setAgreementLoading(false));
    }
  }, [user, token]);

  const handleSave = async () => {
    if (!name.trim()) { setError('Name cannot be empty'); return; }
    if (/\d/.test(name)) { setError('Name cannot contain numeric values'); return; }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post(`${API}/auth/update-profile`, { name: name.trim() }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Refresh auth context with updated user
      login({ ...user, name: res.data.name || name.trim() }, token);
      setSuccess('Profile updated successfully!');
      setEditing(false);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeactivating(true);
    setError('');
    try {
      await axios.post(`${API}/auth/delete-account`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowDeleteModal(false);
      logout();
      alert('Your account has been deactivated successfully.');
      navigate('/login');
    } catch (err) {
      setError(err.response?.data?.msg || err.response?.data?.detail || 'Failed to delete account.');
      setShowDeleteModal(false);
    } finally {
      setDeactivating(false);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    const clean = phone.toString().trim();
    if (clean.startsWith('+91')) return clean;
    if (clean.startsWith('91') && clean.length === 12) return `+${clean}`;
    return `+91 ${clean}`;
  };

  if (!user) return null;

  const initials = (user.name || user.email || 'U').charAt(0).toUpperCase();
  const joinedDate = user.createdAt
    ? new Date(user.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <div style={{ minHeight: 'calc(100vh - 72px)', background: '#F8FAF7', padding: '3rem 1.5rem', fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: 860, margin: '0 auto' }}>

        <div style={{ marginBottom: '1.5rem' }}>
          <BackButton />
        </div>

        {/* Page header */}
        <div style={{ marginBottom: '2.5rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 900, color: '#161616', margin: 0 }}>My Profile</h1>
          <p style={{ color: '#374151', fontWeight: 500, marginTop: '6px', fontSize: '0.95rem' }}>Manage your account details</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,2fr)', gap: '1.5rem', alignItems: 'start' }}>

          {/* Left: Avatar card */}
          <div style={{ background: 'white', borderRadius: '20px', border: '1.5px solid #EEF2E6', padding: '2rem', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
            {/* Avatar */}
            <div style={{
              width: 96, height: 96, borderRadius: '50%',
              background: 'linear-gradient(135deg, #084734, #0a5e44)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '2.5rem', fontWeight: 900, color: '#CEF17B',
              margin: '0 auto 1rem',
            }}>
              {initials}
            </div>

            <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#161616', marginBottom: '4px' }}>{user.name || '—'}</div>
            <div style={{ fontSize: '0.85rem', color: '#374151', fontWeight: 500, marginBottom: '1.5rem' }}>{user.email || user.phone}</div>

            {/* Account info */}
            <div style={{ background: '#F8FAF7', borderRadius: '12px', padding: '1rem', textAlign: 'left' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>Account Info</div>
              <InfoRow label="Role" value={user.role === 'user' ? 'Customer' : user.role} />
              <InfoRow label="Status" value="Active" valueColor="#065F46" />
              {joinedDate && <InfoRow label="Joined" value={joinedDate} />}
            </div>
          </div>

          {/* Right: Edit form */}
          <div>
            {/* Personal details card */}
            <div style={{ background: 'white', borderRadius: '20px', border: '1.5px solid #EEF2E6', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#161616', margin: 0 }}>Personal Details</h2>
                {!editing && (
                  <button onClick={() => setEditing(true)} style={{ background: 'none', border: '1.5px solid #EEF2E6', borderRadius: '8px', padding: '6px 14px', fontWeight: 700, fontSize: '0.82rem', color: '#084734', cursor: 'pointer' }}>
                    ✏️ Edit
                  </button>
                )}
              </div>

              {success && (
                <div style={{ background: '#D1FAE5', color: '#065F46', padding: '10px 14px', borderRadius: '10px', marginBottom: '1rem', fontSize: '0.88rem', fontWeight: 700 }}>
                  ✓ {success}
                </div>
              )}
              {error && (
                <div style={{ background: '#FEE2E2', color: '#DC2626', padding: '10px 14px', borderRadius: '10px', marginBottom: '1rem', fontSize: '0.88rem', fontWeight: 700 }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <Field label="Full Name" editing={editing}>
                  {editing ? (
                    <input
                      value={name}
                      onChange={e => { setName(e.target.value); setError(''); }}
                      style={inputStyle}
                      autoFocus
                    />
                  ) : (
                    <div style={staticField}>{user.name || '—'}</div>
                  )}
                </Field>

                <Field label="Phone Number">
                  <div style={{ ...staticField, background: '#F8FAF7', color: '#374151' }}>{user.phone ? formatPhone(user.phone) : '—'}</div>
                </Field>

                <Field label="Email Address">
                  <div style={{ ...staticField, background: '#F8FAF7', color: '#374151' }}>{user.email || '—'}</div>
                </Field>

                <Field label="Account Type">
                  <div style={{ ...staticField, background: '#F8FAF7', color: '#374151' }}>Customer</div>
                </Field>
              </div>

              {editing && (
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{ background: '#084734', color: '#CEF17B', border: 'none', padding: '12px 24px', borderRadius: '10px', fontWeight: 800, fontSize: '0.9rem', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.65 : 1 }}
                  >
                    {saving ? 'Saving...' : '💾 Save Changes'}
                  </button>
                  <button
                    onClick={() => { setEditing(false); setName(user.name || ''); setError(''); }}
                    style={{ background: 'none', border: '1.5px solid #EEF2E6', borderRadius: '10px', padding: '12px 24px', fontWeight: 700, fontSize: '0.9rem', color: '#666', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {/* Change password card */}
            <div style={{ background: 'white', borderRadius: '20px', border: '1.5px solid #EEF2E6', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#161616', margin: '0 0 0.75rem' }}>🔐 Change Password</h2>
              <p style={{ color: '#374151', fontSize: '0.88rem', fontWeight: 500, margin: '0 0 1rem' }}>
                To change your password, use the forgot password flow — we'll send an OTP to your email.
              </p>
              <button
                onClick={() => navigate('/forgot-password')}
                style={{ background: 'none', border: '1.5px solid #084734', borderRadius: '10px', padding: '10px 20px', fontWeight: 700, fontSize: '0.88rem', color: '#084734', cursor: 'pointer' }}
              >
                Reset Password via Email OTP →
              </button>
            </div>

            {/* Digitally Signed Agreement Card */}
            <div style={{ background: 'white', borderRadius: '20px', border: '1.5px solid #EEF2E6', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h2 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#161616', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Shield size={20} color="#084734" /> Digital Legal Agreement
                </h2>
                <span style={{ background: '#DCEFB8', color: '#084734', fontSize: '0.75rem', fontWeight: '800', padding: '4px 10px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <CheckCircle2 size={12} color="#084734" /> Digitally Signed
                </span>
              </div>
              <p style={{ color: '#374151', fontSize: '0.88rem', fontWeight: 500, margin: '0 0 1rem', lineHeight: 1.5 }}>
                Your signed <strong>Customer Service & Data Privacy Agreement</strong> covers how your personal data, payment gateways, and bank refund details are protected on TurfX.
              </p>
              
              {agreement && (
                <div style={{ background: '#F8FAF7', border: '1px solid #EEF2E6', borderRadius: '12px', padding: '10px 14px', marginBottom: '1.25rem', fontSize: '0.82rem', color: '#4B5563' }}>
                  <div>Certificate ID: <strong style={{ color: '#161616' }}>{agreement.signature_id}</strong></div>
                  <div style={{ marginTop: '2px' }}>Signed On: <strong>{agreement.metadata?.signed_at_ist || new Date(agreement.signed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong></div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setShowAgreementModal(true)}
                  style={{
                    background: '#084734',
                    color: '#CEF17B',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '10px 18px',
                    fontWeight: '800',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <FileText size={16} /> View Agreement
                </button>
                {agreement?.download_url && (
                  <a
                    href={agreement.download_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      background: 'none',
                      border: '1.5px solid #084734',
                      color: '#084734',
                      borderRadius: '10px',
                      padding: '10px 18px',
                      fontWeight: '800',
                      fontSize: '0.85rem',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Download size={16} /> Download Signed PDF
                  </a>
                )}
              </div>
            </div>

            {/* Bank details card */}
            <BankDetailsForm token={token} required={true} />

            {/* Quick links */}
            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button onClick={() => navigate('/my-bookings')} style={{ background: '#F8FAF7', border: '1.5px solid #EEF2E6', borderRadius: '10px', padding: '10px 18px', fontWeight: 700, fontSize: '0.85rem', color: '#161616', cursor: 'pointer' }}>
                📋 My Bookings
              </button>
              <button onClick={() => navigate('/explore')} style={{ background: '#F8FAF7', border: '1.5px solid #EEF2E6', borderRadius: '10px', padding: '10px 18px', fontWeight: 700, fontSize: '0.85rem', color: '#161616', cursor: 'pointer' }}>
                🏟 Explore Venues
              </button>
            </div>

            {/* Delete Account / Danger Zone card */}
            <div style={{ background: '#FEF2F2', borderRadius: '20px', border: '1.5px solid #FCA5A5', padding: '1.5rem', marginTop: '1.5rem', boxShadow: '0 4px 20px rgba(220,38,38,0.05)' }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#991B1B', margin: '0 0 0.5rem' }}>⚠️ Danger Zone</h2>
              <p style={{ color: '#7F1D1D', fontSize: '0.88rem', fontWeight: 500, margin: '0 0 1rem', lineHeight: 1.5 }}>
                Want to delete your account? This action will deactivate your account and log you out immediately.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  style={{ background: '#DC2626', border: 'none', borderRadius: '10px', padding: '10px 20px', fontWeight: 800, fontSize: '0.88rem', color: 'white', cursor: 'pointer' }}
                >
                  Delete Account
                </button>
                <button
                  onClick={() => navigate('/account-deletion')}
                  style={{ background: 'none', border: '1.5px solid #FCA5A5', borderRadius: '10px', padding: '10px 18px', fontWeight: 700, fontSize: '0.85rem', color: '#991B1B', cursor: 'pointer' }}
                >
                  View Account Deletion Guide →
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: '1.5rem', animation: 'fadeIn 0.2s ease-out'
        }}>
          <div style={{
            background: 'white', borderRadius: '24px', maxWidth: '440px', width: '100%',
            padding: '2rem', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
          }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#FEE2E2', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem', margin: '0 auto 1.25rem' }}>
              ⚠️
            </div>
            <h3 style={{ fontSize: '1.3rem', fontWeight: 900, color: '#161616', margin: '0 0 0.5rem' }}>
              Delete Your Account?
            </h3>
            <p style={{ color: '#4B5563', fontSize: '0.92rem', lineHeight: 1.5, marginBottom: '1.75rem', fontWeight: 500 }}>
              Are you sure you want to delete your account? You will be logged out immediately and your access to TurfX will be disabled.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleDeleteAccount}
                disabled={deactivating}
                style={{
                  flex: 1, background: '#DC2626', color: 'white', border: 'none',
                  padding: '12px', borderRadius: '12px', fontWeight: 800, fontSize: '0.92rem',
                  cursor: deactivating ? 'not-allowed' : 'pointer', opacity: deactivating ? 0.7 : 1
                }}
              >
                {deactivating ? 'Deleting...' : 'Yes, Delete Account'}
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deactivating}
                style={{
                  flex: 1, background: '#F3F4F6', color: '#374151', border: 'none',
                  padding: '12px', borderRadius: '12px', fontWeight: 700, fontSize: '0.92rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Agreement Modal */}
      <AgreementModal
        isOpen={showAgreementModal}
        onClose={() => setShowAgreementModal(false)}
        agreementType="customer"
        userAgreement={agreement}
      />
    </div>
  );
}

function InfoRow({ label, value, valueColor }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
      <span style={{ fontSize: '0.8rem', color: '#374151', fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: '0.82rem', fontWeight: 700, color: valueColor || '#374151' }}>{value}</span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '12px 14px', borderRadius: '10px',
  border: '1.5px solid #084734', fontSize: '0.95rem',
  fontWeight: 600, color: '#161616', background: 'white',
  outline: 'none', boxSizing: 'border-box',
};

const staticField = {
  padding: '12px 14px', borderRadius: '10px',
  border: '1.5px solid #EEF2E6', fontSize: '0.95rem',
  fontWeight: 600, color: '#161616', background: '#FAFAF9',
};
